/**
 * Self-contained browser-wallet layer for the collateral-withdrawal flow — the ONLY on-chain code
 * in the console. The operator connects their wallet (the registered collateral-admin EOA) and
 * signs + broadcasts `withdrawAsset` over the injected EIP-1193 provider; the backend never holds
 * the key. viem is intentionally scoped to this module (no wagmi/walletconnect precedent in the
 * repo — keep the surface minimal).
 */
import { createPublicClient, createWalletClient, custom, parseAbi } from "viem";
import type { Address, EIP1193Provider, Hex } from "viem";
import { base } from "viem/chains";
import type { UnsignedWithdrawAsset } from "@/api/collateralWithdrawals";

/**
 * V1 `RainCollateralController.withdrawAsset` — the `bytes32 salt` form, verified against on-chain
 * bytecode in elios-poc (selector 0xe167d26a).
 */
const WITHDRAW_ASSET_ABI = parseAbi([
  "function withdrawAsset(address proxy, address asset, uint256 amount, address recipient, uint256 expiresAt, bytes32 salt, bytes signature)",
]);

export interface BroadcastResult {
  txHash: Hex;
  status: "confirmed" | "reverted";
}

export function hasInjectedWallet(): boolean {
  return getProviderOrNull() !== null;
}

function getProviderOrNull(): EIP1193Provider | null {
  const injected = (window as unknown as { ethereum?: EIP1193Provider }).ethereum;
  return injected ?? null;
}

function requireProvider(): EIP1193Provider {
  const provider = getProviderOrNull();
  if (!provider) {
    throw new Error(
      "No browser wallet found. Install MetaMask (or a compatible EIP-1193 wallet) and connect the collateral-admin account.",
    );
  }
  return provider;
}

/** Prompts the wallet to connect and returns the selected account (the admin EOA). */
export async function connectWallet(): Promise<Address> {
  const walletClient = createWalletClient({ transport: custom(requireProvider()) });
  const [account] = await walletClient.requestAddresses();
  if (!account) {
    throw new Error("No account was authorized in the wallet.");
  }
  return account;
}

/**
 * Signs + broadcasts the unsigned `withdrawAsset`, waits for the Base receipt, and returns the tx
 * hash + terminal outcome. Ensures the wallet is on the collateral chain (Base) first. Every arg is
 * passed exactly as the executor signed it — the amount is native units, the salt is `bytes32`.
 */
export async function sendWithdrawAsset(
  unsigned: UnsignedWithdrawAsset,
  account: Address,
): Promise<BroadcastResult> {
  if (unsigned.chainId !== base.id) {
    throw new Error(
      `Unexpected collateral chain id ${unsigned.chainId} (only Base ${base.id} is supported).`,
    );
  }

  const provider = requireProvider();
  const walletClient = createWalletClient({
    account,
    chain: base,
    transport: custom(provider),
  });
  const publicClient = createPublicClient({ chain: base, transport: custom(provider) });

  const currentChainId = await walletClient.getChainId();
  if (currentChainId !== base.id) {
    await walletClient.switchChain({ id: base.id });
  }

  const txHash = await walletClient.writeContract({
    account,
    chain: base,
    address: unsigned.target as Address,
    abi: WITHDRAW_ASSET_ABI,
    functionName: "withdrawAsset",
    args: [
      unsigned.collateralProxy as Address,
      unsigned.token as Address,
      BigInt(unsigned.amount),
      unsigned.recipient as Address,
      BigInt(unsigned.expiresAt),
      unsigned.salt as Hex,
      unsigned.signature as Hex,
    ],
  });

  const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
  return {
    txHash,
    status: receipt.status === "success" ? "confirmed" : "reverted",
  };
}
